import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Pencil, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const FORM_CODE = 'FT-AMB-CEO';
const FORM_VERSION = '0';
const EMCALI_FORM_CODE = 'FT-AMB-EMCALI';
const EMCALI_FORM_VERSION = '0';

const CEO_MULTI_OPTIONS = {
  tension_levels: ['Nivel 1 (220 V)', 'Nivel 2 (13.2 kV)', 'Nivel 3 (34.5 kV)', 'Nivel 4 (115 kV)'],
  habit: ['Arbol con altura < 5 m', 'Arbol con altura >= 5 m'],
  propagation: ['Semillas', 'Estacas', 'Rebrotes'],
  uses: ['Frutal', 'Ornamental', 'Proteccion hidrica', 'Lena y carbon', 'Maderables', 'Cercas vivas', 'Medicinal', 'Sombrio', 'Postes'],
  anthropic_intervention: ['Poda', 'Tala'],
  electric_line_margin: ['Derecha', 'Izquierda'],
  owners: ['Si', 'No'],
};

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    id: '',
    report_date: todayYmd(),
    species_number: '',
    municipality: '',
    village_name: '',
    servitude_distance_m: '',
    common_name: '',
    family_name: '',
    genus_name: '',
    zone_name: '',
    circuit_or_node: '',
    address_text: '',
    work_order: '',
    species_name: '',
    intervention_type: '',
    quantity: '',
    tension_levels: [],
    habit: [],
    propagation: [],
    uses: [],
    anthropic_intervention: [],
    electric_line_margin: [],
    geo_x: '',
    geo_y: '',
    owners: [],
    tree_height_m: '',
    growth_range_m: '',
    stem_diameter_cm: '',
    owner_name: '',
    volume_text: '',
    circuit_name: '',
    operator_name: 'SENERFI S.A.S',
    report_number: '',
    transformer_number: '',
    vehicle_plate: '',
    transformer_node: '',
    pruner_name: '',
    neighborhood_name: '',
    transformer_power: '',
    driver_name: '',
    transformer_address: '',
    sheet_number: '',
    sheet_total: '',
    item_number: '',
    report_reference: '',
    initial_node_section: '',
    final_node_section: '',
    photo_before_no: '',
    photo_after_no: '',
    photo_before_file: null,
    photo_after_file: null,
    existing_photo_before_url: '',
    existing_photo_before_name: '',
    existing_photo_before_path: '',
    existing_photo_after_url: '',
    existing_photo_after_name: '',
    existing_photo_after_path: '',
    species_common_name: '',
    trees_pruned_count: '',
    tree_plate_observation: '',
    observations: '',
  };
}

function fmtDateDisplay(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '-';
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseStoredArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  const raw = String(value || '').trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map((item) => String(item).trim()).filter(Boolean) : [];
  } catch (_) {
    return raw.split(',').map((item) => item.trim()).filter(Boolean);
  }
}

function joinArray(values) {
  return Array.isArray(values) && values.length ? values.join(', ') : '-';
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-sm font-semibold text-emerald-800 uppercase tracking-wide border-b border-emerald-100 pb-2">{children}</div>;
}

function MultiSelectChips({ label, field, options, values, onToggle, onAddCustom }) {
  const [customValue, setCustomValue] = useState('');

  const addCustom = () => {
    const val = String(customValue || '').trim();
    if (!val) return;
    onAddCustom(field, val);
    setCustomValue('');
  };

  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="text-sm font-medium text-gray-800">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = values.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(field, option)}
              className={`px-3 py-1.5 rounded-full border text-xs ${active ? 'text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
              style={active ? { background: 'linear-gradient(90deg, #059669 0%, #0D9488 100%)' } : undefined}
            >
              {option}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          className="flex-1 min-w-[220px] border rounded px-3 py-2 bg-white"
          placeholder={`Agregar opción en ${label.toLowerCase()}`}
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
        />
        <button
          type="button"
          onClick={addCustom}
          className="px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50"
        >
          Agregar
        </button>
      </div>
      <div className="text-xs text-gray-500">Seleccionados: {joinArray(values)}</div>
    </div>
  );
}

export default function BasePodasAmbientalModule({ location, podaType = 'ceo' }) {
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  const podaLabel = useMemo(() => (podaType === 'emcali' ? 'EMCALI' : 'CEO'), [podaType]);
  const isCeo = podaType === 'ceo';
  const locationLabel = useMemo(() => {
    if (location === 'popayan') return 'Popayan';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.sgi.basePodas.list({ location, poda_type: podaType, limit: 1000, order: 'desc' });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Error cargando base podas', err);
      setFeedback('No se pudo cargar la base de podas.');
    } finally {
      setLoading(false);
    }
  }, [location, podaType]);

  useEffect(() => {
    setForm(emptyForm());
    setFeedback('');
    loadRows();
  }, [location, podaType, loadRows]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isCeo) {
      if (!form.report_date || !form.species_number.trim() || !form.municipality.trim() || !form.species_name.trim()) {
        setFeedback('En CEO completa fecha, numero especie, municipio y especie.');
        return;
      }
    } else if (!form.report_date || !form.report_number.trim() || !form.zone_name.trim()) {
      setFeedback('En EMCALI completa fecha de ejecución, número de reporte y zona.');
      return;
    }
    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.basePodas.upsert({
        ...form,
        location,
        poda_type: podaType,
      });
      setForm(emptyForm());
      setFeedback(`Registro guardado correctamente en ${podaLabel}.`);
      await loadRows();
    } catch (err) {
      console.error('Error guardando base podas', err);
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setForm({
      id: row.id || '',
      report_date: row.report_date || todayYmd(),
      species_number: row.species_number || '',
      municipality: row.municipality || '',
      village_name: row.village_name || '',
      servitude_distance_m: row.servitude_distance_m || '',
      common_name: row.common_name || '',
      family_name: row.family_name || '',
      genus_name: row.genus_name || '',
      zone_name: row.zone_name || '',
      circuit_or_node: row.circuit_or_node || '',
      address_text: row.address_text || '',
      work_order: row.work_order || '',
      species_name: row.species_name || '',
      intervention_type: row.intervention_type || '',
      quantity: row.quantity ?? '',
      tension_levels: parseStoredArray(row.tension_levels_json),
      habit: parseStoredArray(row.habit_json),
      propagation: parseStoredArray(row.propagation_json),
      uses: parseStoredArray(row.uses_json),
      anthropic_intervention: parseStoredArray(row.anthropic_intervention_json),
      electric_line_margin: parseStoredArray(row.electric_line_margin_json),
      geo_x: row.geo_x || '',
      geo_y: row.geo_y || '',
      owners: parseStoredArray(row.owners_json),
      tree_height_m: row.tree_height_m || '',
      growth_range_m: row.growth_range_m || '',
      stem_diameter_cm: row.stem_diameter_cm || '',
      owner_name: row.owner_name || '',
      volume_text: row.volume_text || '',
      circuit_name: row.circuit_name || '',
      operator_name: row.operator_name || 'SENERFI S.A.S',
      report_number: row.report_number || '',
      transformer_number: row.transformer_number || '',
      vehicle_plate: row.vehicle_plate || '',
      transformer_node: row.transformer_node || '',
      pruner_name: row.pruner_name || '',
      neighborhood_name: row.neighborhood_name || '',
      transformer_power: row.transformer_power || '',
      driver_name: row.driver_name || '',
      transformer_address: row.transformer_address || '',
      sheet_number: row.sheet_number || '',
      sheet_total: row.sheet_total || '',
      item_number: row.item_number || '',
      report_reference: row.report_reference || '',
      initial_node_section: row.initial_node_section || '',
      final_node_section: row.final_node_section || '',
      photo_before_no: row.photo_before_no || '',
      photo_after_no: row.photo_after_no || '',
      photo_before_file: null,
      photo_after_file: null,
      existing_photo_before_url: row.photo_before_url || '',
      existing_photo_before_name: row.photo_before_name || '',
      existing_photo_before_path: row.photo_before_path || '',
      existing_photo_after_url: row.photo_after_url || '',
      existing_photo_after_name: row.photo_after_name || '',
      existing_photo_after_path: row.photo_after_path || '',
      species_common_name: row.species_common_name || '',
      trees_pruned_count: row.trees_pruned_count || '',
      tree_plate_observation: row.tree_plate_observation || '',
      observations: row.observations || '',
    });
    setFeedback(`Editando registro #${row.id} de ${podaLabel}.`);
  };

  const toggleMultiValue = (field, value) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = current.includes(value);
      return {
        ...prev,
        [field]: exists ? current.filter((item) => item !== value) : [...current, value],
      };
    });
  };

  const addCustomMultiValue = (field, value) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      if (current.includes(value)) return prev;
      return { ...prev, [field]: [...current, value] };
    });
  };

  const openPreview = (row, startType = 'before') => {
    const images = [];
    if (row.photo_before_url) images.push({ url: row.photo_before_url, label: 'Foto antes' });
    if (row.photo_after_url) images.push({ url: row.photo_after_url, label: 'Foto después' });
    if (!images.length) return;
    const index = Math.max(0, images.findIndex((item) => item.label === (startType === 'after' ? 'Foto después' : 'Foto antes')));
    setPreviewImage({ images, index });
  };

  const movePreview = (direction) => {
    setPreviewImage((prev) => {
      if (!prev || !Array.isArray(prev.images) || !prev.images.length) return prev;
      const nextIndex = prev.index + direction;
      if (nextIndex < 0 || nextIndex >= prev.images.length) return prev;
      return { ...prev, index: nextIndex };
    });
  };

  useEffect(() => {
    if (!previewImage?.images?.length) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        movePreview(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        movePreview(1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewImage]);

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro #${row.id} de ${podaLabel}?`);
    if (!ok) return;
    try {
      await api.sgi.basePodas.delete({ id: row.id });
      if (String(form.id) === String(row.id)) setForm(emptyForm());
      setFeedback('Registro eliminado.');
      await loadRows();
    } catch (err) {
      console.error('Error eliminando base podas', err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const exportExcelCeo = async () => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const bodyRows = rows.map((row) => `
        <tr>
          <td>${escapeHtml(row.species_number || '')}</td>
          <td>${escapeHtml(row.municipality || '')}</td>
          <td>${escapeHtml(row.village_name || '')}</td>
          <td>${escapeHtml(fmtDateDisplay(row.report_date))}</td>
          <td>${escapeHtml(row.servitude_distance_m || '')}</td>
          <td>${escapeHtml(row.common_name || '')}</td>
          <td>${escapeHtml(row.family_name || '')}</td>
          <td>${escapeHtml(row.genus_name || '')}</td>
          <td>${escapeHtml(row.species_name || '')}</td>
          <td>${escapeHtml(row.tree_height_m || '')}</td>
          <td>${escapeHtml(row.growth_range_m || '')}</td>
          <td>${escapeHtml(row.stem_diameter_cm || '')}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.tension_levels_json)))}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.habit_json)))}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.propagation_json)))}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.uses_json)))}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.anthropic_intervention_json)))}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.electric_line_margin_json)))}</td>
          <td>${escapeHtml(row.geo_x || '')}</td>
          <td>${escapeHtml(row.geo_y || '')}</td>
          <td>${escapeHtml(joinArray(parseStoredArray(row.owners_json)))}</td>
          <td>${escapeHtml(row.owner_name || '')}</td>
          <td>${escapeHtml(row.volume_text || '')}</td>
          <td>${escapeHtml(row.circuit_name || '')}</td>
          <td>${escapeHtml(row.observations || '')}</td>
        </tr>
      `).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>Base podas CEO</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 10pt; vertical-align: middle; word-wrap: break-word; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; }
              .meta-value { text-align: center; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 180px; max-height: 80px; }
              .w-sm { width: 85px; }
              .w-md { width: 120px; }
              .w-lg { width: 150px; }
              .w-xl { width: 190px; }
            </style>
          </head>
          <body>
            <table>
              <colgroup>
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-sm" />
                <col class="w-sm" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-sm" />
                <col class="w-md" />
                <col class="w-sm" />
                <col class="w-lg" />
                <col class="w-lg" />
                <col class="w-md" />
                <col class="w-xl" />
                <col class="w-lg" />
                <col class="w-md" />
                <col class="w-sm" />
                <col class="w-sm" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-md" />
                <col class="w-xl" />
              </colgroup>
              <tr>
                <td class="logo-cell" colspan="4" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="16">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label" colspan="3">CODIGO:</td>
                <td class="meta-value" colspan="2">${escapeHtml(FORM_CODE)}</td>
              </tr>
              <tr>
                <td class="title" colspan="16">SISTEMA DE GESTION AMBIENTAL</td>
                <td class="meta-label" colspan="3">VERSION:</td>
                <td class="meta-value" colspan="2">${escapeHtml(FORM_VERSION)}</td>
              </tr>
              <tr>
                <td class="title" colspan="8">BASE DE PODAS CEO</td>
                <td class="title" colspan="8">SEDE: ${escapeHtml(locationLabel)}</td>
                <td class="meta-label" colspan="3">FECHA:</td>
                <td class="meta-value" colspan="2">${escapeHtml(fmtDateDisplay(todayYmd()))}</td>
              </tr>
              <tr>
                <th class="header">NUMERO ESPECIE</th>
                <th class="header">MUNICIPIO</th>
                <th class="header">VEREDA / CORREGIMIENTO</th>
                <th class="header">FECHA</th>
                <th class="header">DIST. SERVIDUMBRE (M)</th>
                <th class="header">NOMBRE COMUN</th>
                <th class="header">FAMILIA</th>
                <th class="header">GENERO</th>
                <th class="header">ESPECIE</th>
                <th class="header">ALTURA (M)</th>
                <th class="header">RANGO CRECIMIENTO</th>
                <th class="header">DIAMETRO TALLO (CM)</th>
                <th class="header">NIVEL DE TENSION</th>
                <th class="header">HABITO</th>
                <th class="header">PROPAGACION</th>
                <th class="header">USOS</th>
                <th class="header">INTERVENCION ANTROPICA</th>
                <th class="header">MARGEN LINEA ELECTRICA</th>
                <th class="header">COORDENADA X</th>
                <th class="header">COORDENADA Y</th>
                <th class="header">PROPIETARIO</th>
                <th class="header">NOMBRE PROPIETARIO</th>
                <th class="header">VOLUMEN</th>
                <th class="header">CIRCUITO</th>
                <th class="header">OBSERVACIONES</th>
              </tr>
              ${bodyRows || '<tr><td colspan="25" style="text-align:center;">Sin registros</td></tr>'}
            </table>
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `base_podas_ceo_${location || 'sede'}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Base podas CEO' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando base podas CEO', err);
      setFeedback('No se pudo exportar el archivo de Excel de CEO.');
    }
  };

  const exportExcelEmcali = async () => {
    try {
      const firstRow = rows[0] || {};
      const bodyRows = rows.map((row) => `
        <tr>
          <td>${escapeHtml(row.item_number || '')}</td>
          <td>${escapeHtml(row.address_text || '')}</td>
          <td>${escapeHtml(row.report_reference || row.report_number || '')}</td>
          <td>${escapeHtml(row.initial_node_section || '')}</td>
          <td>${escapeHtml(row.final_node_section || '')}</td>
          <td>${escapeHtml(row.photo_before_name || row.photo_before_no || '')}</td>
          <td>${escapeHtml(row.photo_after_name || row.photo_after_no || '')}</td>
          <td>${escapeHtml(row.species_common_name || '')}</td>
          <td>${escapeHtml(row.trees_pruned_count || '')}</td>
          <td>${escapeHtml(row.tree_plate_observation || row.observations || '')}</td>
        </tr>
      `).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>Base podas EMCALI</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; }
              td, th { border: 1px solid #000; padding: 6px 7px; font-size: 10pt; vertical-align: middle; word-wrap: break-word; }
              .title { text-align: center; font-weight: 700; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .meta-label { font-weight: 700; background: #f8f8f8; }
              .meta-value { text-align: left; }
              .logo-text { font-size: 16pt; font-weight: 700; color: #c62828; text-align: center; }
              .small { font-size: 8.5pt; }
              .w-xs { width: 70px; }
              .w-sm { width: 95px; }
              .w-md { width: 120px; }
              .w-lg { width: 150px; }
              .w-xl { width: 190px; }
              .w-xxl { width: 220px; }
            </style>
          </head>
          <body>
            <table>
              <colgroup>
                <col class="w-xs" />
                <col class="w-xxl" />
                <col class="w-md" />
                <col class="w-lg" />
                <col class="w-lg" />
                <col class="w-sm" />
                <col class="w-sm" />
                <col class="w-lg" />
                <col class="w-sm" />
                <col class="w-xxl" />
              </colgroup>
              <tr>
                <td class="logo-text" colspan="2" rowspan="2">EMCALI</td>
                <td class="title" colspan="8">EMCALI EICE ESP</td>
              </tr>
              <tr>
                <td class="title" colspan="8">REGISTRO DE PODAS DE ALUMBRADO PUBLICO EJECUTADAS</td>
              </tr>
              <tr>
                <td class="meta-label small" colspan="2">NOMBRE DEL OPERADOR/CONTRATISTA:</td>
                <td class="meta-value" colspan="8">${escapeHtml(firstRow.operator_name || 'SENERFI S.A.S')}</td>
              </tr>
              <tr>
                <td class="meta-label small" colspan="2">NUMERO DE REPORTE</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.report_number || '')}</td>
                <td class="meta-label small" colspan="2">NUMERO DEL TRAFO</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.transformer_number || '')}</td>
                <td class="meta-label small">PLACA VEHICULO</td>
                <td class="meta-value">${escapeHtml(firstRow.vehicle_plate || '')}</td>
              </tr>
              <tr>
                <td class="meta-label small" colspan="2">FECHA DE EJECUCION</td>
                <td class="meta-value" colspan="2">${escapeHtml(fmtDateDisplay(firstRow.report_date || todayYmd()))}</td>
                <td class="meta-label small" colspan="2">NODO DEL TRAFO</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.transformer_node || '')}</td>
                <td class="meta-label small">NOMBRE PODADOR</td>
                <td class="meta-value">${escapeHtml(firstRow.pruner_name || '')}</td>
              </tr>
              <tr>
                <td class="meta-label small" colspan="2">BARRIO / VEREDA</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.neighborhood_name || '')}</td>
                <td class="meta-label small" colspan="2">POTENCIA DEL TRAFO</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.transformer_power || '')}</td>
                <td class="meta-label small">NOMBRE CONDUCTOR</td>
                <td class="meta-value">${escapeHtml(firstRow.driver_name || '')}</td>
              </tr>
              <tr>
                <td class="meta-label small" colspan="2">ZONA</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.zone_name || '')}</td>
                <td class="meta-label small" colspan="2">DIRECCION DEL TRAFO</td>
                <td class="meta-value" colspan="2">${escapeHtml(firstRow.transformer_address || '')}</td>
                <td class="meta-label small">HOJA</td>
                <td class="meta-value">${escapeHtml(firstRow.sheet_number || '')} ${firstRow.sheet_total ? `DE ${escapeHtml(firstRow.sheet_total)}` : ''}</td>
              </tr>
              <tr>
                <td class="meta-label small">CODIGO</td>
                <td class="meta-value">${escapeHtml(EMCALI_FORM_CODE)}</td>
                <td class="meta-label small">VERSION</td>
                <td class="meta-value">${escapeHtml(EMCALI_FORM_VERSION)}</td>
                <td class="meta-label small">SEDE</td>
                <td class="meta-value" colspan="2">${escapeHtml(locationLabel)}</td>
                <td class="meta-label small">FECHA EXPORTACION</td>
                <td class="meta-value" colspan="2">${escapeHtml(fmtDateDisplay(todayYmd()))}</td>
              </tr>
              <tr>
                <th class="header">ITEM</th>
                <th class="header">DIRECCION</th>
                <th class="header">REPORTE</th>
                <th class="header">NODO INICIAL TRAMO PODADO</th>
                <th class="header">NODO FINAL TRAMO PODADO O FECHA</th>
                <th class="header">No. FOTO ANTES</th>
                <th class="header">No. FOTO DESPUES</th>
                <th class="header">ESPECIE ARBOREA / NOMBRE COMUN</th>
                <th class="header">CANTIDAD DE ARBOLES PODADO</th>
                <th class="header">PLACA ARBOL PODADO / OBSERVACIONES</th>
              </tr>
              ${bodyRows || '<tr><td colspan="10" style="text-align:center;">Sin registros</td></tr>'}
            </table>
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `base_podas_emcali_${location || 'sede'}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Base podas EMCALI' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando base podas EMCALI', err);
      setFeedback('No se pudo exportar el archivo de Excel de EMCALI.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-base font-semibold text-gray-800">Base de podas {podaLabel}</div>
          <div className="text-sm text-gray-600">
            {isCeo ? 'Inventario de especies forestales CEO con formato detallado.' : `Registro y seguimiento operativo de podas para ${podaLabel}.`}
          </div>
        </div>
        {!IS_SIG_MOBILE && (
          <button
            type="button"
            onClick={isCeo ? exportExcelCeo : exportExcelEmcali}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110"
            style={{ background: 'linear-gradient(90deg, #059669 0%, #16A34A 100%)', borderColor: '#047857', color: '#FFFFFF' }}
          >
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        {isCeo ? (
          <>
            <SectionTitle>Inventario De Especies Forestales</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Numero especie (senalizacion)">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.species_number} onChange={(e) => setForm((prev) => ({ ...prev, species_number: e.target.value }))} />
              </Field>
              <Field label="Municipio">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.municipality} onChange={(e) => setForm((prev) => ({ ...prev, municipality: e.target.value }))} />
              </Field>
              <Field label="Vereda o corregimiento">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.village_name} onChange={(e) => setForm((prev) => ({ ...prev, village_name: e.target.value }))} />
              </Field>
              <Field label="Fecha">
                <input type="date" className="w-full border rounded px-3 py-2" value={form.report_date} onChange={(e) => setForm((prev) => ({ ...prev, report_date: e.target.value }))} />
              </Field>
              <Field label="Distancia de servidumbre (m)">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.servitude_distance_m} onChange={(e) => setForm((prev) => ({ ...prev, servitude_distance_m: e.target.value }))} />
              </Field>
              <Field label="Nombre comun">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.common_name} onChange={(e) => setForm((prev) => ({ ...prev, common_name: e.target.value }))} />
              </Field>
              <Field label="Familia">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.family_name} onChange={(e) => setForm((prev) => ({ ...prev, family_name: e.target.value }))} />
              </Field>
              <Field label="Genero">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.genus_name} onChange={(e) => setForm((prev) => ({ ...prev, genus_name: e.target.value }))} />
              </Field>
              <Field label="Especie">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.species_name} onChange={(e) => setForm((prev) => ({ ...prev, species_name: e.target.value }))} />
              </Field>
              <Field label="Altura del arbol (m)">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.tree_height_m} onChange={(e) => setForm((prev) => ({ ...prev, tree_height_m: e.target.value }))} />
              </Field>
              <Field label="Rango de crecimiento">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.growth_range_m} onChange={(e) => setForm((prev) => ({ ...prev, growth_range_m: e.target.value }))} />
              </Field>
              <Field label="Diametro de tallo (cm)">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.stem_diameter_cm} onChange={(e) => setForm((prev) => ({ ...prev, stem_diameter_cm: e.target.value }))} />
              </Field>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <MultiSelectChips label="Nivel de tension" field="tension_levels" options={CEO_MULTI_OPTIONS.tension_levels} values={form.tension_levels} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Habito" field="habit" options={CEO_MULTI_OPTIONS.habit} values={form.habit} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Propagacion" field="propagation" options={CEO_MULTI_OPTIONS.propagation} values={form.propagation} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Usos" field="uses" options={CEO_MULTI_OPTIONS.uses} values={form.uses} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Intervencion antropica" field="anthropic_intervention" options={CEO_MULTI_OPTIONS.anthropic_intervention} values={form.anthropic_intervention} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Margen de la linea electrica" field="electric_line_margin" options={CEO_MULTI_OPTIONS.electric_line_margin} values={form.electric_line_margin} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
              <MultiSelectChips label="Propietario" field="owners" options={CEO_MULTI_OPTIONS.owners} values={form.owners} onToggle={toggleMultiValue} onAddCustom={addCustomMultiValue} />
            </div>

            <SectionTitle>Georreferenciacion Y Propietario</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Coordenada X">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.geo_x} onChange={(e) => setForm((prev) => ({ ...prev, geo_x: e.target.value }))} />
              </Field>
              <Field label="Coordenada Y">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.geo_y} onChange={(e) => setForm((prev) => ({ ...prev, geo_y: e.target.value }))} />
              </Field>
              <Field label="Nombre del propietario">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.owner_name} onChange={(e) => setForm((prev) => ({ ...prev, owner_name: e.target.value }))} />
              </Field>
              <Field label="Volumen">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.volume_text} onChange={(e) => setForm((prev) => ({ ...prev, volume_text: e.target.value }))} />
              </Field>
              <Field label="Circuito">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.circuit_name} onChange={(e) => setForm((prev) => ({ ...prev, circuit_name: e.target.value }))} />
              </Field>
              <Field label="Observaciones">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.observations} onChange={(e) => setForm((prev) => ({ ...prev, observations: e.target.value }))} />
              </Field>
            </div>
          </>
        ) : (
          <>
            <SectionTitle>Encabezado Del Registro EMCALI</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Nombre del operador / contratista">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.operator_name} onChange={(e) => setForm((prev) => ({ ...prev, operator_name: e.target.value }))} />
              </Field>
              <Field label="Numero de reporte">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.report_number} onChange={(e) => setForm((prev) => ({ ...prev, report_number: e.target.value }))} />
              </Field>
              <Field label="Numero del trafo">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.transformer_number} onChange={(e) => setForm((prev) => ({ ...prev, transformer_number: e.target.value }))} />
              </Field>
              <Field label="Placa vehiculo">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.vehicle_plate} onChange={(e) => setForm((prev) => ({ ...prev, vehicle_plate: e.target.value }))} />
              </Field>
              <Field label="Fecha de ejecucion">
                <input type="date" className="w-full border rounded px-3 py-2" value={form.report_date} onChange={(e) => setForm((prev) => ({ ...prev, report_date: e.target.value }))} />
              </Field>
              <Field label="Nodo del trafo">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.transformer_node} onChange={(e) => setForm((prev) => ({ ...prev, transformer_node: e.target.value }))} />
              </Field>
              <Field label="Nombre podador">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.pruner_name} onChange={(e) => setForm((prev) => ({ ...prev, pruner_name: e.target.value }))} />
              </Field>
              <Field label="Barrio / vereda">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.neighborhood_name} onChange={(e) => setForm((prev) => ({ ...prev, neighborhood_name: e.target.value }))} />
              </Field>
              <Field label="Potencia del trafo">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.transformer_power} onChange={(e) => setForm((prev) => ({ ...prev, transformer_power: e.target.value }))} />
              </Field>
              <Field label="Nombre conductor">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.driver_name} onChange={(e) => setForm((prev) => ({ ...prev, driver_name: e.target.value }))} />
              </Field>
              <Field label="Zona">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.zone_name} onChange={(e) => setForm((prev) => ({ ...prev, zone_name: e.target.value }))} />
              </Field>
              <Field label="Direccion del trafo">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.transformer_address} onChange={(e) => setForm((prev) => ({ ...prev, transformer_address: e.target.value }))} />
              </Field>
              <Field label="Hoja">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.sheet_number} onChange={(e) => setForm((prev) => ({ ...prev, sheet_number: e.target.value }))} />
              </Field>
              <Field label="De">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.sheet_total} onChange={(e) => setForm((prev) => ({ ...prev, sheet_total: e.target.value }))} />
              </Field>
            </div>

            <SectionTitle>Detalle De Poda Ejecutada</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Item">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.item_number} onChange={(e) => setForm((prev) => ({ ...prev, item_number: e.target.value }))} />
              </Field>
              <Field label="Direccion">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.address_text} onChange={(e) => setForm((prev) => ({ ...prev, address_text: e.target.value }))} />
              </Field>
              <Field label="Reporte">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.report_reference} onChange={(e) => setForm((prev) => ({ ...prev, report_reference: e.target.value }))} />
              </Field>
              <Field label="Nodo inicial tramo podado">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.initial_node_section} onChange={(e) => setForm((prev) => ({ ...prev, initial_node_section: e.target.value }))} />
              </Field>
              <Field label="Nodo final tramo podado o fecha">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.final_node_section} onChange={(e) => setForm((prev) => ({ ...prev, final_node_section: e.target.value }))} />
              </Field>
              <Field label="No. foto antes">
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="w-full border rounded px-3 py-2 bg-white"
                    onChange={(e) => setForm((prev) => ({ ...prev, photo_before_file: e.target.files?.[0] || null }))}
                  />
                  <div className="text-xs text-gray-500">Adjunta la imagen del antes.</div>
                  {form.existing_photo_before_url ? (
                    <a href={form.existing_photo_before_url} target="_blank" rel="noreferrer" className="inline-block text-xs text-blue-700 hover:underline">
                      Ver foto actual: {form.existing_photo_before_name || 'Abrir imagen'}
                    </a>
                  ) : null}
                </div>
              </Field>
              <Field label="No. foto despues">
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="w-full border rounded px-3 py-2 bg-white"
                    onChange={(e) => setForm((prev) => ({ ...prev, photo_after_file: e.target.files?.[0] || null }))}
                  />
                  <div className="text-xs text-gray-500">Adjunta la imagen del después.</div>
                  {form.existing_photo_after_url ? (
                    <a href={form.existing_photo_after_url} target="_blank" rel="noreferrer" className="inline-block text-xs text-blue-700 hover:underline">
                      Ver foto actual: {form.existing_photo_after_name || 'Abrir imagen'}
                    </a>
                  ) : null}
                </div>
              </Field>
              <Field label="Especie arborea / nombre comun">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.species_common_name} onChange={(e) => setForm((prev) => ({ ...prev, species_common_name: e.target.value }))} />
              </Field>
              <Field label="Cantidad de arboles podado">
                <input type="text" className="w-full border rounded px-3 py-2" value={form.trees_pruned_count} onChange={(e) => setForm((prev) => ({ ...prev, trees_pruned_count: e.target.value }))} />
              </Field>
              <Field label="Placa arbol podado / observaciones">
                <textarea className="w-full border rounded px-3 py-2 min-h-[42px]" value={form.tree_plate_observation} onChange={(e) => setForm((prev) => ({ ...prev, tree_plate_observation: e.target.value }))} />
              </Field>
            </div>
          </>
        )}

        {!isCeo ? (
          <div>
            <label className="block text-sm font-medium mb-1">Observaciones generales</label>
            <textarea
              className="w-full border rounded px-3 py-2 min-h-[90px]"
              value={form.observations}
              onChange={(e) => setForm((prev) => ({ ...prev, observations: e.target.value }))}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className={`text-sm ${feedback.includes('correctamente') || feedback.includes('eliminado') ? 'text-emerald-700' : 'text-yellow-700'}`}>
            {feedback || `Gestiona los registros de podas de ${podaLabel}.`}
          </div>
          <div className="flex items-center gap-2">
            {form.id ? (
              <button
                type="button"
                onClick={() => setForm(emptyForm())}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancelar edición
              </button>
            ) : null}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-lime-600 text-white hover:bg-lime-700 disabled:opacity-60"
            >
              <Save size={16} /> {saving ? 'Guardando...' : (form.id ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="font-medium text-gray-800">Registros guardados</div>
          <div className="text-sm text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-lime-200 text-gray-900">
              {isCeo ? (
                <tr>
                  <th className="px-3 py-2 text-left">Numero especie</th>
                  <th className="px-3 py-2 text-left">Municipio</th>
                  <th className="px-3 py-2 text-left">Vereda o corregimiento</th>
                  <th className="px-3 py-2 text-left">Nombre comun</th>
                  <th className="px-3 py-2 text-left">Especie</th>
                  <th className="px-3 py-2 text-left">Nivel de tension</th>
                  <th className="px-3 py-2 text-left">Usos</th>
                  <th className="px-3 py-2 text-left">Propietario</th>
                  <th className="px-3 py-2 text-left">Nombre propietario</th>
                  <th className="px-3 py-2 text-left">Circuito</th>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Observaciones</th>
                  <th className="px-3 py-2 text-left">Acciones</th>
                </tr>
              ) : (
                <tr>
                  <th className="px-3 py-2 text-left">Reporte</th>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Zona</th>
                  <th className="px-3 py-2 text-left">Barrio / Vereda</th>
                  <th className="px-3 py-2 text-left">Dirección</th>
                  <th className="px-3 py-2 text-left">Nodo inicial</th>
                  <th className="px-3 py-2 text-left">Nodo final / fecha</th>
                  <th className="px-3 py-2 text-left">Especie</th>
                  <th className="px-3 py-2 text-left">Cant. arboles</th>
                  <th className="px-3 py-2 text-left">Observaciones</th>
                  <th className="px-3 py-2 text-left">Acciones</th>
                </tr>
              )}
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={isCeo ? 13 : 10} className="px-3 py-6 text-center text-gray-500">
                    No hay registros de podas para {podaLabel}.
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-t">
                  {isCeo ? (
                    <>
                      <td className="px-3 py-2">{row.species_number || '-'}</td>
                      <td className="px-3 py-2">{row.municipality || '-'}</td>
                      <td className="px-3 py-2">{row.village_name || '-'}</td>
                      <td className="px-3 py-2">{row.common_name || '-'}</td>
                      <td className="px-3 py-2">{row.species_name || '-'}</td>
                      <td className="px-3 py-2">{joinArray(parseStoredArray(row.tension_levels_json))}</td>
                      <td className="px-3 py-2">{joinArray(parseStoredArray(row.uses_json))}</td>
                      <td className="px-3 py-2">{joinArray(parseStoredArray(row.owners_json))}</td>
                      <td className="px-3 py-2">{row.owner_name || '-'}</td>
                      <td className="px-3 py-2">{row.circuit_name || '-'}</td>
                      <td className="px-3 py-2">{fmtDateDisplay(row.report_date)}</td>
                      <td className="px-3 py-2">{row.observations || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2">{row.report_number || '-'}</td>
                      <td className="px-3 py-2">{fmtDateDisplay(row.report_date)}</td>
                      <td className="px-3 py-2">{row.zone_name || '-'}</td>
                      <td className="px-3 py-2">{row.neighborhood_name || '-'}</td>
                      <td className="px-3 py-2">{row.address_text || '-'}</td>
                      <td className="px-3 py-2">{row.initial_node_section || '-'}</td>
                      <td className="px-3 py-2">{row.final_node_section || '-'}</td>
                      <td className="px-3 py-2">{row.species_common_name || '-'}</td>
                      <td className="px-3 py-2">{row.trees_pruned_count || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="space-y-1">
                          <div>{row.tree_plate_observation || row.observations || '-'}</div>
                          {(row.photo_before_url || row.photo_after_url) ? (
                            <div className="flex flex-wrap gap-3 pt-1">
                              {row.photo_before_url ? (
                                <button
                                  type="button"
                                  onClick={() => openPreview(row, 'before')}
                                  className="block text-left"
                                >
                                  <img
                                    src={row.photo_before_url}
                                    alt="Foto antes"
                                    className="h-16 w-16 rounded border border-gray-300 object-cover"
                                  />
                                  <span className="mt-1 block text-xs text-blue-700 hover:underline">Foto antes</span>
                                </button>
                              ) : null}
                              {row.photo_after_url ? (
                                <button
                                  type="button"
                                  onClick={() => openPreview(row, 'after')}
                                  className="block text-left"
                                >
                                  <img
                                    src={row.photo_after_url}
                                    alt="Foto después"
                                    className="h-16 w-16 rounded border border-gray-300 object-cover"
                                  />
                                  <span className="mt-1 block text-xs text-blue-700 hover:underline">Foto después</span>
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          {row.photo_before_url ? (
                            <a href={row.photo_before_url} target="_blank" rel="noreferrer" className="block text-blue-700 hover:underline">
                              Foto antes
                            </a>
                          ) : null}
                          {row.photo_after_url ? (
                            <a href={row.photo_after_url} target="_blank" rel="noreferrer" className="block text-blue-700 hover:underline">
                              Foto después
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil size={14} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {previewImage?.images?.[previewImage.index]?.url ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-5xl w-full rounded-xl bg-white p-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-800">{previewImage.images[previewImage.index]?.label || 'Vista previa'}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => movePreview(-1)}
                  disabled={previewImage.index <= 0}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => movePreview(1)}
                  disabled={previewImage.index >= previewImage.images.length - 1}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Siguiente
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
            <div className="flex justify-center overflow-auto">
              <img
                src={previewImage.images[previewImage.index]?.url}
                alt={previewImage.images[previewImage.index]?.label || 'Vista previa'}
                className="max-h-[80vh] w-auto max-w-full rounded border border-gray-200 object-contain"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
